// Rate cards (managers only): a day rate per category, per project, or per category on one project. When an asset
// arrives at a project site the deployment takes the most specific card — project + category, then project, then
// category, then the account default — and keeps that rate; change a running deployment on the project page.
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/session';
import { Empty, Loading, LoadError, Page } from '../../components/Page';
import {
  type RateCard,
  useDeleteRateCard,
  useProjects,
  useRateCards,
  useSaveRateCard,
} from '../../data/billing';
import { useCategories } from '../../data/queries';
import { localeTag } from '../../lib/time';

function RateDialog({ card, onClose }: { card?: RateCard; onClose(): void }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const categories = useCategories();
  const projects = useProjects();
  const save = useSaveRateCard();
  const remove = useDeleteRateCard();
  const [categoryId, setCategoryId] = useState(card?.category_id ?? '');
  const [projectId, setProjectId] = useState(card?.project_id ?? '');
  const [rate, setRate] = useState(card ? Number(card.day_rate).toFixed(2) : '');
  const [currency, setCurrency] = useState(card?.currency ?? 'MYR');
  const n = Number(rate);
  const valid =
    rate.trim() !== '' && Number.isFinite(n) && n >= 0 && /^[A-Za-z]{3}$/.test(currency);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        id: card?.id,
        companyId: me!.company!.id,
        category_id: categoryId || null,
        project_id: projectId || null,
        day_rate: n,
        currency,
      },
      { onSuccess: onClose },
    );
  };
  const error = (save.error ?? remove.error) as (Error & { code?: string }) | null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>{card ? t('rates.edit') : t('rates.new')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              select
              label={t('assets.category')}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <MenuItem value="">{t('rates.any_category')}</MenuItem>
              {(categories.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('rates.project')}
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <MenuItem value="">{t('rates.any_project')}</MenuItem>
              {(projects.data ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                type="number"
                label={t('rates.day_rate')}
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                required
                fullWidth
                autoFocus
              />
              <TextField
                label={t('billing.currency')}
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                slotProps={{ htmlInput: { maxLength: 3 } }}
                sx={{ width: 110 }}
              />
            </Stack>
            {error ? (
              <Alert severity="error">
                {error.code === '23505' ? t('rates.duplicate') : error.message}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          {card ? (
            <Button
              color="error"
              sx={{ mr: 'auto' }}
              disabled={remove.isPending}
              onClick={() => remove.mutate(card.id, { onSuccess: onClose })}
            >
              {t('rates.delete')}
            </Button>
          ) : null}
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={!valid || save.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function RatesPage() {
  const { t, i18n } = useTranslation();
  const cards = useRateCards();
  const [editing, setEditing] = useState<RateCard | 'new' | null>(null);
  const fmt = new Intl.NumberFormat(localeTag(i18n.language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // most specific first — the order the deployment clock picks them in
  const sorted = [...(cards.data ?? [])].sort(
    (a, b) =>
      Number(!!b.project_id) - Number(!!a.project_id) ||
      Number(!!b.category_id) - Number(!!a.category_id),
  );
  return (
    <Page
      title={t('rates.title')}
      actions={
        <Button variant="contained" onClick={() => setEditing('new')}>
          {t('rates.new')}
        </Button>
      }
    >
      <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
        {t('rates.intro')}
      </Typography>
      {cards.isLoading ? (
        <Loading />
      ) : cards.error ? (
        <LoadError error={cards.error} />
      ) : !sorted.length ? (
        <Empty>{t('rates.none')}</Empty>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('rates.project')}</TableCell>
              <TableCell>{t('assets.category')}</TableCell>
              <TableCell align="right">{t('rates.day_rate')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.projects?.name ?? t('rates.any_project')}</TableCell>
                <TableCell>{c.asset_categories?.name ?? t('rates.any_category')}</TableCell>
                <TableCell align="right">
                  {fmt.format(Number(c.day_rate))} {c.currency}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setEditing(c)}>
                    {t('common.edit')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing ? (
        <RateDialog
          card={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Page>
  );
}
